import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Award, Search, ChevronLeft, ChevronRight,
  ShieldOff, ShieldCheck, Trash2, Plus, Eye, X,
} from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_LABELS = { issued: 'Délivré', revoked: 'Révoqué', expired: 'Expiré' };
const STATUS_COLORS = {
  issued:  'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  revoked: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  expired: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
};

const PAGE_SIZE = 25;

// ─── Modal : délivrance manuelle ────────────────────────────────────────────
const IssueModal = ({ onClose, onSuccess }) => {
  const [users, setUsers]     = useState([]);
  const [courses, setCourses] = useState([]);
  const [form, setForm]       = useState({ userId: '', courseId: '', score: 100, note: '' });
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get('/api/admin/users', { params: { limit: 100, page: 1 } }),
      axios.get('/api/admin/courses', { params: { limit: 100 } }),
    ]).then(([u, c]) => {
      setUsers(u.data.users || []);
      setCourses(c.data.courses || []);
    }).catch(() => toast.error('Erreur chargement données'));
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.userId || !form.courseId) { toast.error('Sélectionnez un utilisateur et un cours'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post('/api/admin/certificates/issue', {
        userId:   form.userId,
        courseId: form.courseId,
        score:    Number(form.score),
        note:     form.note || undefined,
      });
      if (data.success) { toast.success('Certificat délivré'); onSuccess(); onClose(); }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur délivrance');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Délivrer un certificat manuellement</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Utilisateur</label>
            <select
              value={form.userId}
              onChange={e => setForm(f => ({ ...f, userId: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              required
            >
              <option value="">-- Sélectionner --</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}{u.email ? ` (${u.email})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cours</label>
            <select
              value={form.courseId}
              onChange={e => setForm(f => ({ ...f, courseId: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              required
            >
              <option value="">-- Sélectionner --</option>
              {courses.map(c => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Score attribué (%)
            </label>
            <input
              type="number" min="0" max="100"
              value={form.score}
              onChange={e => setForm(f => ({ ...f, score: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Note interne (optionnel)
            </label>
            <input
              type="text"
              placeholder="Raison, contexte…"
              value={form.note}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900">
              Annuler
            </button>
            <button
              type="submit" disabled={saving}
              className="px-5 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Délivrance…' : 'Délivrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Page principale ─────────────────────────────────────────────────────────
const AdminCertificates = () => {
  const navigate = useNavigate();

  const [certs, setCerts]         = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [actionId, setActionId]   = useState(null);

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, limit: PAGE_SIZE, status: statusFilter };
      if (search) params.search = search;
      const { data } = await axios.get('/api/admin/certificates', { params });
      if (data.success) { setCerts(data.certificates); setTotal(data.total); }
    } catch {
      toast.error('Erreur chargement certificats');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => { setPage(1); }, [statusFilter, search]);
  useEffect(() => { load(page); }, [load, page]);

  const handleStatusChange = async (id, newStatus) => {
    setActionId(id);
    try {
      const { data } = await axios.patch(`/api/admin/certificates/${id}/status`, { status: newStatus });
      if (data.success) {
        setCerts(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c));
        toast.success(`Certificat ${STATUS_LABELS[newStatus].toLowerCase()}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement ce certificat ? Cette action est irréversible.')) return;
    setActionId(id);
    try {
      await axios.delete(`/api/admin/certificates/${id}`);
      setCerts(prev => prev.filter(c => c.id !== id));
      setTotal(prev => prev - 1);
      toast.success('Certificat supprimé');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur suppression');
    } finally {
      setActionId(null);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-3">
              <Award className="w-8 h-8 text-primary" /> Certificats
            </h1>
            <p className="text-gray-500 dark:text-gray-400">{total} certificat{total !== 1 ? 's' : ''} au total</p>
          </div>
          <button
            onClick={() => setShowIssueModal(true)}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Délivrer manuellement
          </button>
        </div>

        {/* Filtres */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par étudiant ou cours…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
          >
            <option value="all">Tous les statuts</option>
            <option value="issued">Délivrés</option>
            <option value="revoked">Révoqués</option>
            <option value="expired">Expirés</option>
          </select>
        </div>

        {/* Tableau */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : certs.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <Award className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aucun certificat trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-300">Étudiant</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-300 hidden md:table-cell">Cours</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-300">Score</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-300">Statut</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-300 hidden sm:table-cell">Émis le</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-300 hidden lg:table-cell">Code</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {certs.map(cert => {
                    const holder = cert.holder;
                    const holderName = holder
                      ? `${holder.first_name || ''} ${holder.last_name || ''}`.trim() || '—'
                      : cert.metadata?.holder_name || '—';
                    const isManual = cert.metadata?.issued_manually;
                    const busy = actionId === cert.id;

                    return (
                      <tr key={cert.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="py-3 px-4">
                          <div className="font-medium text-gray-900 dark:text-white">{holderName}</div>
                          {isManual && (
                            <span className="text-xs text-yellow-600 dark:text-yellow-400">Manuel</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-300 hidden md:table-cell">
                          {cert.course?.title || cert.metadata?.course_title || '—'}
                        </td>
                        <td className="py-3 px-4 font-semibold text-primary">
                          {Math.round(Number(cert.score || 0))}%
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[cert.status] || STATUS_COLORS.issued}`}>
                            {STATUS_LABELS[cert.status] || cert.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-xs text-gray-500 hidden sm:table-cell">
                          {cert.issued_at ? new Date(cert.issued_at).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        <td className="py-3 px-4 hidden lg:table-cell">
                          <span className="font-mono text-xs text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                            {cert.verification_code}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => navigate(`/certificates/${cert.id}`)}
                              className="p-1.5 text-gray-400 hover:text-primary"
                              title="Voir le certificat"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {cert.status === 'issued' ? (
                              <button
                                onClick={() => handleStatusChange(cert.id, 'revoked')}
                                disabled={busy}
                                className="p-1.5 text-orange-500 hover:text-orange-700 disabled:opacity-40"
                                title="Révoquer"
                              >
                                <ShieldOff className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleStatusChange(cert.id, 'issued')}
                                disabled={busy}
                                className="p-1.5 text-green-500 hover:text-green-700 disabled:opacity-40"
                                title="Réactiver"
                              >
                                <ShieldCheck className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(cert.id)}
                              disabled={busy}
                              className="p-1.5 text-red-500 hover:text-red-700 disabled:opacity-40"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-500">Page {page} / {totalPages}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showIssueModal && (
        <IssueModal
          onClose={() => setShowIssueModal(false)}
          onSuccess={() => load(1)}
        />
      )}
    </div>
  );
};

export default AdminCertificates;
